import React, { useEffect, useRef, useState } from 'react';
import { Send, RefreshCw, MessageSquare } from 'lucide-react';
import { SupportAPI } from '../../api';
import { toast } from 'sonner';
import CustomerPanel from './CustomerPanel';
import CannedReplyPicker from './CannedReplyPicker';

const fmt = (ts) => (ts ? new Date(ts).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');

const SupportInbox = () => {
  const [convos, setConvos] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [thread, setThread] = useState(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [customerFailed, setCustomerFailed] = useState(false);
  const endRef = useRef(null);
  const inputRef = useRef(null);

  const loadConvos = () => SupportAPI.conversations().then(setConvos).catch((e) => { setConvos([]); toast.error(e?.response?.data?.detail || 'Failed to load conversations'); });
  useEffect(() => { loadConvos(); const t = setInterval(loadConvos, 15000); return () => clearInterval(t); }, []);

  useEffect(() => {
    if (!openId) { setThread(null); return; }
    let alive = true;
    const load = () => SupportAPI.conversation(openId).then((d) => alive && setThread(d)).catch(() => {});
    load();
    const t = setInterval(load, 6000);
    return () => { alive = false; clearInterval(t); };
  }, [openId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [thread]);

  // One customer lookup per conversation, shared by the info panel and the saved-reply placeholders.
  useEffect(() => {
    if (!openId) { setCustomer(null); return undefined; }
    let alive = true;
    setCustomer(null);
    setCustomerFailed(false);
    SupportAPI.customer(openId)
      .then((d) => alive && setCustomer(d))
      .catch((e) => { console.error('[support] customer lookup failed', e); if (alive) setCustomerFailed(true); });
    return () => { alive = false; };
  }, [openId]);

  // The composer grows with the message so a long saved reply is fully visible before sending.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [draft]);

  const insertCanned = (text) => {
    setDraft((d) => (d.trim() ? `${d.replace(/\s*$/, '')} ${text}` : text));
    inputRef.current?.focus();
  };

  const reply = async (e) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body) return;
    setSending(true);
    try {
      const m = await SupportAPI.reply(openId, body);
      setThread((t) => ({ ...t, messages: [...(t?.messages || []), m] }));
      setDraft('');
      loadConvos();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send the reply');
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="support-inbox">
      <div className="flex items-center justify-between mb-4">
        <p className="text-[13px] text-[#6F76A7]">Conversations from users who asked for help in the workspace.</p>
        <button onClick={loadConvos} data-testid="support-refresh" className="inline-flex items-center gap-1.5 text-[13px] text-[#0A1551] hover:underline"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
      </div>

      {convos === null ? <p className="text-[13px] text-[#6F76A7]">Loading…</p> : convos.length === 0 ? (
        <div className="bg-white border border-[#E3E5F5] rounded-xl p-10 text-center" data-testid="support-empty">
          <MessageSquare className="w-8 h-8 mx-auto text-[#B0B4D0] mb-2" />
          <p className="text-[14px] text-[#6F76A7]">No one has asked for help yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] xl:grid-cols-[280px_1fr_260px] gap-4">
          <div className="bg-white border border-[#E3E5F5] rounded-xl overflow-hidden max-h-[520px] overflow-y-auto">
            {convos.map((c) => (
              <button key={c.id} onClick={() => setOpenId(c.id)} data-testid={`support-convo-${c.id}`}
                className={`w-full text-left px-4 py-3 border-b border-[#E3E5F5] last:border-0 hover:bg-[#F9FAFD] ${openId === c.id ? 'bg-[#F0F1FB]' : ''}`}>
                <div className="flex items-center gap-2">
                  <span className="text-[13px] font-semibold text-[#0A1551] truncate flex-1">{c.user_name || c.user_email}</span>
                  {(c.mode || 'bot') === 'bot'
                    ? <span className="px-1.5 py-0.5 rounded-full bg-[#EEF2FF] text-[#4A517D] text-[10px] font-bold" data-testid={`support-mode-${c.id}`}>BOT</span>
                    : <span className="px-1.5 py-0.5 rounded-full bg-[#FDF0E6] text-[#B45309] text-[10px] font-bold" data-testid={`support-mode-${c.id}`}>HUMAN</span>}
                  {c.unread_for_admin > 0 && <span className="px-1.5 py-0.5 rounded-full bg-[#E5387B] text-white text-[10px] font-bold" data-testid={`support-unread-${c.id}`}>{c.unread_for_admin}</span>}
                </div>
                <p className="text-[12px] text-[#6F76A7] truncate">{c.last_message || 'No messages yet'}</p>
                <p className="text-[11px] text-[#9599C0] mt-0.5">{fmt(c.updated_at)}</p>
              </button>
            ))}
          </div>

          <div className="bg-white border border-[#E3E5F5] rounded-xl flex flex-col h-[520px]">
            {!thread ? (
              <p className="m-auto text-[13px] text-[#6F76A7]">Pick a conversation to read and reply.</p>
            ) : (
              <>
                <div className="px-4 py-3 border-b border-[#E3E5F5]">
                  <p className="text-[14px] font-bold text-[#0A1551]">{thread.conversation.user_name || thread.conversation.user_email}</p>
                  <p className="text-[12px] text-[#6F76A7]">{thread.conversation.user_email}</p>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="support-thread">
                  {thread.messages.map((m) => (
                    <div key={m.id} className={`flex ${m.sender === 'agent' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[75%] px-3 py-2 rounded-lg text-[13px] ${m.sender === 'agent' ? 'bg-[#0F766E] text-white' : m.sender === 'bot' ? 'bg-[#EEF2FF] text-[#0A1551]' : 'bg-[#F0F1FB] text-[#0A1551]'}`}>
                        {m.sender === 'bot' && <div className="text-[11px] font-semibold text-[#4A517D] mb-0.5">{m.sender_name || 'Assistant'}</div>}
                        {m.body}
                        <div className={`text-[10px] mt-1 ${m.sender === 'agent' ? 'text-white/70' : 'text-[#9599C0]'}`}>{fmt(m.created_at)}</div>
                      </div>
                    </div>
                  ))}
                  <div ref={endRef} />
                </div>
                <form onSubmit={reply} className="p-3 border-t border-[#E3E5F5] flex items-end gap-2">
                  <textarea ref={inputRef} value={draft} onChange={(e) => setDraft(e.target.value)} rows={1}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); reply(e); } }}
                    placeholder="Write a reply, or use a saved one" data-testid="support-reply-input"
                    className="flex-1 px-3 py-2 border border-[#D5D7ED] rounded-md text-[14px] leading-[1.45] resize-none overflow-y-auto focus:outline-none focus:border-[#0A1551]" />
                  <CannedReplyPicker customer={customer} onPick={insertCanned} />
                  <button type="submit" disabled={!draft.trim() || sending} data-testid="support-reply-send"
                    className="p-2.5 rounded-md bg-[#0F766E] text-white disabled:opacity-50 transition-colors" aria-label="Send reply"><Send className="w-4 h-4" /></button>
                </form>
              </>
            )}
          </div>

          {openId && <CustomerPanel customer={customer} failed={customerFailed} />}
        </div>
      )}
    </div>
  );
};

export default SupportInbox;
